<template>
  <aside>
    <v-navigation-drawer
      app
      clipped
      :temporary="$vuetify.breakpoint.mobile"
      :value="isDrawerShown"
      @input="toggleDrawer"
    >
      <!-- Top-tier users (platform/application/organization) reach every child of
           their default org, so the lazy subtree rooted there already shows their
           whole scope and scales to large trees. -->
      <template v-if="showRootedOrgTree">
        <OrganizationTree
          v-model="orgTreeExpanded"
          :class="{
            'restricted-user': isRestrictedUser,
            'my-1': !isFacilityManager,
          }"
          :root-org="rootTreeOrganization"
          :root-action="gotoDefaultPage"
          leaf-type="facility"
          :current-org-id="currentFacilityId"
          :subheader="currentFacility ? currentFacility.name : ''"
          @action="select"
        />

        <v-divider />
      </template>

      <!-- Region/facility managers: show their region(s) and facilities as a tree
           built from the bounded accessible scope. Covers a single-region manager
           (their region + its facilities) as well as cross-region managers; a lone
           single-facility user gets no tree. -->
      <template v-else-if="reachesMultipleFacilities">
        <div
          :class="{
            'restricted-user': isRestrictedUser,
            'my-1': !isFacilityManager,
          }"
        >
          <SideMenuOrgTree
            v-for="root in accessibleOrgTree"
            :key="root.id"
            :node="root"
            leaf-type="facility"
            :current-org-id="currentFacilityId"
            @action="select"
          />
        </div>

        <v-divider />
      </template>

      <SideMenuList />
    </v-navigation-drawer>
  </aside>
</template>

<script>
import SideMenuList from "./SideMenuList.vue";
import SideMenuOrgTree from "./SideMenuOrgTree.vue";
import OrganizationTree from "../utils/OrganizationTree.vue";

import useNavigation from "./useNavigation";
import useFacility from "../authentication/useFacility";
import useUser from "../authentication/useUser";
import useAccessibleScope from "../authentication/useAccessibleScope";
import useAlerts from "@tod-ui/composables/useAlerts";
import { computed, ref } from "vue";

// Default orgs at or above the organization tier own their whole subtree, so the
// lazy rooted tree covers their scope. Region/facility managers instead get the
// ancestor-path tree built from their accessible scope.
const ROOTED_TREE_ORG_TYPES = ["platform", "application", "organization"];

export default {
  name: "TheSideMenu",
  components: {
    SideMenuList,
    SideMenuOrgTree,
    OrganizationTree,
  },
  setup() {
    const {
      defaultOrganization,
      isFacilityManager,
      defaultPage,
      currentUser,
      loadChatbot,
    } = useUser();
    const {
      reachesMultipleFacilities,
      accessibleOrgTree,
      singleFacilityScope,
      hasSystemScope,
      topLevelOrganization,
    } = useAccessibleScope();
    const { currentFacility, currentFacilityId, selectFacility } =
      useFacility();
    const { toggleDrawer, isDrawerShown } = useNavigation(true);

    const isRestrictedUser = computed(() => {
      return currentUser.value?.username === "cpotash@citylinecapital.com";
    });

    const showRootedOrgTree = computed(
      () =>
        (hasSystemScope.value ||
          ROOTED_TREE_ORG_TYPES.includes(defaultOrganization.value?.type)) &&
        !singleFacilityScope.value
    );

    // A system admin's rooted tree starts at the platform/application org their
    // role grants, not their default facility, so the lazy tree spans the whole
    // system. Everyone else roots at their default organization.
    const rootTreeOrganization = computed(() =>
      hasSystemScope.value
        ? topLevelOrganization.value
        : defaultOrganization.value
    );

    const orgTreeExpanded = ref(false);

    const { clearAllAlerts } = useAlerts();

    return {
      currentFacility,
      currentFacilityId,
      selectFacility,
      defaultOrganization,
      isFacilityManager,
      defaultPage,
      toggleDrawer,
      isDrawerShown,
      orgTreeExpanded,
      clearAllAlerts,
      isRestrictedUser,
      loadChatbot,
      currentUser,
      showRootedOrgTree,
      rootTreeOrganization,
      reachesMultipleFacilities,
      accessibleOrgTree,
    };
  },
  watch: {
    "$vuetify.breakpoint.mobile": function (val) {
      this.toggleDrawer(!val);
    },
  },
  beforeMount() {
    this.loadChatbotScript();
  },
  methods: {
    select(facility) {
      this.clearAllAlerts();
      // Establish the facility context, then navigate straight to its dashboard
      // WITH the organization_id param. Pushing { name: "Facility" } without it
      // triggers vue-router "missing param" warnings (Facility + its redirect to
      // FacilityDashboard both require organization_id).
      this.selectFacility(this.$apollo, facility);
      this.$router.push({
        name: "FacilityDashboard",
        params: { organization_id: facility.id },
      });
      this.orgTreeExpanded = false;
    },
    gotoDefaultPage() {
      if (this.$route.name !== this.defaultPage.name) {
        this.$router.push(this.defaultPage);
      }
    },
    loadChatbotScript() {
      this.loadChatbot(this.$apollo, this.currentUser);
    },
  },
};
</script>

<style lang="scss" scoped>
aside {
  border-right: 1px solid rgba(0, 0, 0, 0.09);
}

.theme--dark aside {
  border-right-color: rgba(255, 255, 255, 0.12);
}

/* Slim, theme-aware scrollbar for the drawer — Vuetify exposes no prop for
   scrollbar styling, so this is the one spot that stays as CSS. */
::v-deep .v-navigation-drawer__content {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }
}

.theme--dark ::v-deep .v-navigation-drawer__content {
  scrollbar-color: rgba(255, 255, 255, 0.24) transparent;

  &::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.24);
  }
}

/*restricted user style */
.restricted-user {
  pointer-events: none;
  opacity: 0.6;
}

.restricted-user .v-list-item--link {
  cursor: not-allowed !important;
  opacity: 0.5 !important;
}

@media print {
  aside {
    display: none;
  }
}
</style>
